import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoadingSnackbarService, SnackMessageNames } from '../../../shared/services/loading-snackbar.service';
import { IScheduleResponse } from '../../data-rules-model';
import { ScheduleService } from './schedule.service';

export declare type ScheduleDataToSave = Pick<ScheduleData, 'dataRulesID'|'startTime'|'stopTime'|'period'|'scheduleUnit'|'enabled'>;

export class ScheduleData {
  dataRulesID: string;
  startTime: string;
  stopTime: string;
  period: number;
  scheduleUnit: string;
  enabled: boolean;

  public get isExpired(): boolean {
    const dateNow = new Date();
    return this.stopTime && dateNow > new Date(this.stopTime);
  }

  public get ruleExpression(): string {
    return `${this.period} ${this.scheduleUnit}`;
  }

  public get dataToSave(): ScheduleDataToSave {
    return {
      dataRulesID: this.dataRulesID,
      startTime: this.startTime,
      stopTime: this.stopTime,
      period: this.period,
      scheduleUnit: this.scheduleUnit,
      enabled: this.enabled
    };
  }

  constructor(
    dataRulesID: string,
    startTime: string,
    stopTime: string,
    period: number,
    scheduleUnit: string,
    enabled: boolean,
  ) {
    this.dataRulesID = dataRulesID;
    this.startTime = startTime;
    this.stopTime = stopTime;
    this.period = period;
    this.scheduleUnit = scheduleUnit;
    this.enabled = enabled;
  }
}

export declare type ScheduleRate = [string, number];

abstract class AbstractScheduleDataService {

  public get allSchedules(): ScheduleData[] {
    return this.isNoSchedules || !this.isInitialisedOnce ? [] : [...this._schedules];
  }

  public get isInitialisedOnce(): boolean {
    return typeof this._schedules !== 'undefined';
  }

  public schedulesUpdated = new EventEmitter<void>();

  // Originally _schedules is undefined and ll never be that then, once it changed to anything of the specified types
  // DO NOT MANUALLY SET THAT PROPERTY TO UNDEFINED ANYWHERE
  private _schedules: ScheduleData[] | null;

  constructor(
    protected loadingSnackbarService: LoadingSnackbarService
  ) {

  }

  private get isNoSchedules(): boolean {
    return this._schedules === null;
  }

  public  initialize(force: boolean): Observable<void> {
    // do load of chains if nothing in the internal data
    if (
        !force && this.isInitialisedOnce
    ) {
      return of(undefined);
    }

    return this.loadSchedules().pipe(
      map((rawData: IScheduleResponse[]) => {
        this._schedules = rawData.length > 0
          ? rawData.map(rawSchedule => {

            const [scheduleUnit, schedulePeriod] = ScheduleService.getScheduleRate(rawSchedule.ruleExpression);

            return new ScheduleData(
              ScheduleService.getScheduledRuleId(rawSchedule.ruleName),
              rawSchedule.startDate,
              rawSchedule.endDate,
              schedulePeriod,
              scheduleUnit,
              rawSchedule.enabled,
            );
          })
          : null;
      })
    );
  }

  // expects only existing string-like ids
  public getScheduleByRuleId(rId: string): ScheduleData | null {
      return this.isNoSchedules ? null : this.allSchedules.find(({dataRulesID}) => rId === dataRulesID);
  }

  public swapScheduleBetweenRules(swapFromRuleId: string, swapToRuleId: string): Observable<void> {
    const scheduleData = this.getScheduleByRuleId(swapFromRuleId);
    this.loadingSnackbarService.show(SnackMessageNames.SWAPPING_SCHEDULES);
    return forkJoin({
      deleteSchedule: this.deleteSchedule(swapFromRuleId),
      saveSchedule: this.saveSchedule(new ScheduleData(
        swapToRuleId,
        scheduleData.startTime,
        scheduleData.stopTime,
        scheduleData.period,
        scheduleData.scheduleUnit,
        scheduleData.enabled
      ))
    }).pipe(
      map(_ => {
        this.schedulesUpdated.emit();
        this.loadingSnackbarService.hide(SnackMessageNames.SWAPPING_SCHEDULES);
      })
    );
  }

  public saveSchedule(savingSchedule: ScheduleData): Observable<void> {
    return this.createSchedule(savingSchedule.dataToSave).pipe(
      map(_ => {
        if (!this._schedules) {
          this._schedules = []; // Initialize if it is null or undefined
        }

        const existingScheduleIndex = this.allSchedules.findIndex(sch => sch.dataRulesID === savingSchedule.dataRulesID);
        if (existingScheduleIndex > -1) {
          this._schedules[existingScheduleIndex] = savingSchedule;
        } else {
          this._schedules.push(savingSchedule);
        }
        this.schedulesUpdated.emit();
      })
    );
  }

  public deleteSchedule(ruleId: string): Observable<void> {
    return this.deleteScheduleRequest(ruleId).pipe(map(_ => {
      const deletedScheduleIndex = this.allSchedules.findIndex(schedule => schedule.dataRulesID === ruleId);
      if (deletedScheduleIndex > -1) {
        this._schedules.splice(deletedScheduleIndex, 1);
      }
      this.schedulesUpdated.emit();
    }));
  }

  protected abstract loadSchedules(): Observable<IScheduleResponse[]>;
  protected abstract createSchedule(schedule: ScheduleDataToSave): Observable<IScheduleResponse>;
  protected abstract deleteScheduleRequest(ruleId: string): Observable<void>;

}

@Injectable({
  providedIn: 'root'
})
export class ScheduleDataService extends AbstractScheduleDataService {

  constructor(private http: HttpClient,
              protected loadingSnackbarService: LoadingSnackbarService) {
    super(loadingSnackbarService);
  }

  protected loadSchedules(): Observable<IScheduleResponse[]> {
    return this.http.get<IScheduleResponse[]>(`${environment.dataRulesUrl}/schedules`);
  }

  protected createSchedule(schedule: ScheduleDataToSave) {
    return this.http.post<any>(`${environment.dataRulesUrl}/schedules`, schedule);
  }

  protected deleteScheduleRequest(ruleId: string): Observable<void> {
    return this.http.delete<void>(`${environment.dataRulesUrl}/schedules/${ruleId}`);
  }
}
